/**
 * SubmitStateRenderer — Loading, error, and partial-success messaging.
 */

import React from 'react';
import {
  Box,
  LinearProgress,
  Button,
  Typography,
  Alert, AlertTitle } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { getAccountingTranslation } from './utils/translation';

/**
 * Translate canned error messages while preserving arbitrary backend messages.
 */
function translateErrorMessage(message: string): string {
  if (!message) return getAccountingTranslation('errors:generic', 'Something went wrong');

  // Translate canned error messages from normalizeLedgerError
  if (message === 'Network error. Please check your connection and try again.') {
    return getAccountingTranslation('errors:networkError', 'Network error. Please check your connection and try again.');
  }
  if (message === 'An unknown error occurred.') {
    return getAccountingTranslation('errors:unknown', 'An unknown error occurred.');
  }

  // Keep arbitrary backend messages readable as supplied
  return message;
}

const useStyles = makeStyles((theme) => ({
  container: {
    marginBottom: theme.spacing(2),
  },
  loading: {
    marginBottom: theme.spacing(1),
  },
  alert: {
    marginBottom: theme.spacing(1),
  },
  alertTitle: {
    fontWeight: 600,
  },
  errorList: {
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(2),
  },
  errorItem: {
    marginBottom: theme.spacing(0.5),
    fontSize: '0.875rem',
  },
  retryButton: {
    marginTop: theme.spacing(1),
  },
}));

export interface SubmitStateRendererProps {
  isSubmitting?: boolean;
  errors?: Record<string, string>;
  fieldErrors?: Record<string, string>;
  queueError?: string | null;
  hasPartialSuccess?: boolean;
  onRetry?: () => void;
}

/**
 * Display loading, error, and partial-success states during submission.
 */
export const SubmitStateRenderer: React.FC<SubmitStateRendererProps> = ({
  isSubmitting = false,
  errors = {},
  fieldErrors = {},
  queueError,
  hasPartialSuccess = false,
  onRetry,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const uniqueErrors = new Set<string>();
  if (queueError) {
    uniqueErrors.add(translateErrorMessage(queueError));
  }
  Object.values(errors).forEach((msg) => {
    uniqueErrors.add(translateErrorMessage(msg));
  });
  Object.values(fieldErrors).forEach((msg) => {
    uniqueErrors.add(translateErrorMessage(msg));
  });

  const hasErrors = uniqueErrors.size > 0;

  if (!isSubmitting && !hasErrors && !hasPartialSuccess) {
    return null;
  }

  return (
    <Box className={classes.container}>
      {isSubmitting && (
        <Box className={classes.loading}>
          <LinearProgress />
          <Typography variant="body2" style={{ marginTop: '8px' }}>
            {getAccountingTranslation('common:processing', 'Processing...')}
          </Typography>
        </Box>
      )}

      {hasPartialSuccess && !isSubmitting && (
        <Alert severity="warning" className={classes.alert}>
          <AlertTitle className={classes.alertTitle}>
            {getAccountingTranslation('submitState:partialSuccess', 'Partial Success')}
          </AlertTitle>
          <Typography variant="body2">
            {getAccountingTranslation('submitState:partialSuccessMessage', 'Some operations succeeded, but others failed.')}
          </Typography>
          {onRetry && (
            <Button
              size="small"
              onClick={onRetry}
              className={classes.retryButton}
            >
              {getAccountingTranslation('common:retry', 'Retry')}
            </Button>
          )}
        </Alert>
      )}

      {hasErrors && !isSubmitting && (
      <Alert severity="error" className={classes.alert}>
        <AlertTitle className={classes.alertTitle}>
          {getAccountingTranslation('submitState:error', 'Error')}
        </AlertTitle>
        {uniqueErrors.size > 0 && (
        <Box className={classes.errorList}>
          {Array.from(uniqueErrors).map((message) => (
            <Typography key={message} variant="body2" className={classes.errorItem}>
              {message}
            </Typography>
          ))}
        </Box>
        )}
      </Alert>
      )}
    </Box>
  );
};

export default SubmitStateRenderer;
